import NextImage from 'next/image'
import Link from 'next/link'
import { cn, SparkleIcon } from '@nwwp/ui-shadcn'
import { CTA } from './CTA'

type ContainerProps = {
  children: React.ReactNode
  className?: string
}

const Container = ({ children, className }: ContainerProps) => (
  <div
    data-partner-card
    className={cn(
      'flex flex-col justify-between',
      'rounded-4xl bg-white',
      'border border-frost shadow-sm',
      'overflow-hidden',
      'p-4 tablet:p-6',
      'tablet:flex-row tablet:items-start tablet:justify-start tablet:gap-4',
      className
    )}
  >
    {children}
  </div>
)

type ImageProps = {
  src: string
  alt: string
}

const Image = ({ src, alt }: ImageProps) => (
  <div
    className={cn(
      'order-last flex w-full items-end justify-center',
      'tablet:order-first tablet:size-[185px] tablet:min-w-[185px] tablet:shrink-0 tablet:items-center'
    )}
  >
    <NextImage
      src={src}
      alt={alt}
      width={185}
      height={185}
      className="h-auto w-full object-contain tablet:size-[185px]"
    />
  </div>
)

type InfoProps = {
  name: string
  bio: string[]
  credential?: string
  profileSlug?: string
}

const Info = ({ name, bio, credential, profileSlug }: InfoProps) => {
  const nameContent = (
    <span
      data-partner-name
      className={`text-body-bold-lg text-charcoal${profileSlug ? ' underline underline-offset-4' : ''}`}
    >
      {name}
      {credential ? <span className="font-normal"> {credential}</span> : null}
    </span>
  )

  const nameElement = profileSlug ? (
    <Link href={`/meet-your-partners/${profileSlug}`} className="underline underline-offset-4">
      {nameContent}
    </Link>
  ) : (
    nameContent
  )

  return (
    <div className="flex flex-col gap-4 tablet:self-start">
      {nameElement}
      <ul className="flex flex-col gap-2">
        {bio.map((item) => (
          <li key={item} data-bio-line className="flex items-start gap-2 text-body-sm text-charcoal">
            <SparkleIcon width={12} height={12} color="var(--color-green-lighter)" className="mt-0.5 shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export const PartnerCard = {
  Container,
  Image,
  Info,
  CTA,
}
